/* eslint-disable @typescript-eslint/no-empty-function */

import { EnvironmentConfig, SourceSystem, UserProfile, UserRoles, isCWInsightsRole } from '../SharedModels';
import { SetStateAction, createContext, useCallback, useEffect, useRef, useState } from 'react';

import ApiHelper from '../ApiHelper';
import { FavoriteReport } from '../../Reports/ReportModels';
import { User } from '../../TeamMembers/TeamMemberModels';
import { useIdleTimer } from 'react-idle-timer';
import { useMsal } from '@azure/msal-react';

export const DefaultUserProfile: UserProfile = {
    favoriteReports: [],
    addFavoriteReport: () => {},
    removeFavoriteReport: () => {},
    fetchFavoriteReports: () => Promise.resolve(),
    waitForProfileLoad: Promise.resolve(),
    cdnToken: '?',
    sourceSystems: [],
    setSourceSystems: () => {},
    isLoading: false,
    setIsLoading: () => {},
    isEccoviaUser: false,
    environmentConfig: {},
    failedToLoad: false,
};
export const ECCOVIA_CUSTOMERID = 'eccovia';

export const UserProfileContext = createContext<UserProfile>(DefaultUserProfile);

export function useUserProfile(): UserProfile {
    const [favoriteReports, setFavoriteReports] = useState<FavoriteReport[]>([]);
    const [sourceSystems, setSourceSystems] = useState<SourceSystem[]>([]);
    const [isLoading, setIsLoadingState] = useState(false);
    const [profileLoading, setProfileLoading] = useState(true);
    const [failedToLoad, setFailedToLoad] = useState(false);
    const [user, setUser] = useState<User>();
    const [environmentConfig, setEnvironmentConfig] = useState<EnvironmentConfig>({});
    const [cdnToken, setCdnToken] = useState('?');

    const initPromiseRef = useRef<Promise<void>>(Promise.resolve());
    const resolveRef = useRef<(() => void) | null>(null);
    const rejectRef = useRef<((reason?: unknown) => void) | null>(null);

    const [api] = useState(new ApiHelper());
    const { instance, accounts } = useMsal();

    const resetProfileLoadPromise = useCallback(() => {
        initPromiseRef.current = new Promise<void>((resolve, reject) => {
            resolveRef.current = resolve;
            rejectRef.current = reject;
        });
    }, []);

    const setIsLoading = useCallback((value: SetStateAction<boolean>) => {
        const nextValue = typeof value === 'function' ? value(isLoading) : value;

        if (nextValue) {
            setProfileLoading(true);
            resetProfileLoadPromise();
        }

        setIsLoadingState(nextValue);
    }, [isLoading, resetProfileLoadPromise]);

    const isEccoviaUser = instance.getAllAccounts()[0]?.idTokenClaims?.extension_customerId === ECCOVIA_CUSTOMERID;

    const onIdle = useCallback(() => {
        const netApiLogout = api.callApi(
            instance,
            [process.env.REACT_APP_B2C_SCOPE ?? ''],
            process.env.REACT_APP_NET_API_URL + '/User/logout',
            'DELETE'
        );

        let pythonApiLogout = Promise.resolve<Response>(new Response());
        if (isUserSourceSystemAdminOrHigher(user, isEccoviaUser)) {
            pythonApiLogout = api.callApi(
                instance,
                [process.env.REACT_APP_B2C_SCOPE ?? ''],
                process.env.REACT_APP_PYTHON_API_URL + '/logout',
                'DELETE'
            );
        }

        Promise.all([netApiLogout, pythonApiLogout]).finally(() => {
            instance.logout();
        });
    }, [api, instance, isEccoviaUser, user]);

    // 1000 (ms) * 60 (seconds) * 30 (minutes)
    useIdleTimer({ onIdle, timeout: 1000 * 60 * 30 });

    const fetchFavoriteReports = useCallback(() => {
        return api.callApi(
            instance,
            [process.env.REACT_APP_B2C_SCOPE ?? ''],
            process.env.REACT_APP_NET_API_URL + '/UserProfile/favreports'
        ).then(async (result: Response) => {
            const favorites: FavoriteReport[] = await result.json();
            setFavoriteReports(favorites);
        });
    }, [api, instance]);

    const initializeUserProfile = useCallback(() => {
        if (accounts.length > 0) {
            setProfileLoading(true);
            const favReportsPromise = fetchFavoriteReports();

            const sourceSystemsPromise = api.callApi(
                instance,
                [process.env.REACT_APP_B2C_SCOPE ?? ''],
                process.env.REACT_APP_NET_API_URL + '/User/sourcesystems/true'
            ).then(async (result: Response) => {
                const sources: SourceSystem[] = await result.json();
                setSourceSystems(sources);
            });

            const cdnTokenPromise = api.callApi(
                instance,
                [process.env.REACT_APP_B2C_SCOPE ?? ''],
                process.env.REACT_APP_NET_API_URL + '/CdnToken'
            ).then(async (result: Response) => {
                const t = await result.text();
                setCdnToken(t);
            });

            const environmentConfigPromise = api.callApi(
                instance,
                [process.env.REACT_APP_B2C_SCOPE ?? ''],
                process.env.REACT_APP_NET_API_URL + '/UserProfile/environmentconfig'
            ).then(async (result: Response) => {
                const env = await result.json();
                setEnvironmentConfig((prev) => {
                    return { ...prev, ...env };
                });
            });

            const customerPromise = api.callApi(
                instance,
                [process.env.REACT_APP_B2C_SCOPE ?? ''],
                process.env.REACT_APP_NET_API_URL + '/User/customer'
            ).then(async (result: Response) => {
                const customer = await result.json();
                setEnvironmentConfig((prev) => {
                    return { ...prev, ...customer };
                });
            });

            // Wait for all API calls to complete before setting loading to false
            return Promise.all([
                favReportsPromise,
                sourceSystemsPromise,
                cdnTokenPromise,
                environmentConfigPromise,
                customerPromise
            ]).then(() => {
                resolveRef.current?.();
                resolveRef.current = null;
                rejectRef.current = null;
            })
            .catch((error) => {
                console.error('Error initializing user profile:', error);
                rejectRef.current?.(error);
                resolveRef.current = null;
                rejectRef.current = null;
            })
            .finally(() => {
                setProfileLoading(false);
            });
        }
        return Promise.resolve();
    }, [accounts.length, api, fetchFavoriteReports, instance]);

    const verifyUserProfileCreation = useCallback(() => {
        if (accounts.length > 0) {
            api.callApi(
                instance,
                [process.env.REACT_APP_B2C_SCOPE ?? ''],
                process.env.REACT_APP_NET_API_URL + '/User/me'
            )
                .then(async (result: Response) => {
                    const user: User = await result.json();
                    setUser(user);
                })
                .catch((error) => {
                    setFailedToLoad(true);
                    rejectRef.current?.(error);
                    resolveRef.current = null;
                    rejectRef.current = null;
                    setProfileLoading(false);
                });
        }
    }, [accounts.length, api, instance]);

    useEffect(() => {
        if (!isLoading && accounts.length > 0) {
            if (user) {
                initializeUserProfile();
            } else {
                verifyUserProfileCreation();
            }
        } else if (isLoading && user) {
            setUser(undefined);
        }
    }, [accounts.length, api, isLoading, initializeUserProfile, instance, user, verifyUserProfileCreation]);

    const saveFavoritesList = useCallback(
        (favorites: FavoriteReport[]) => {
            api.callApi(
                instance,
                [process.env.REACT_APP_B2C_SCOPE ?? ''],
                process.env.REACT_APP_NET_API_URL + `/UserProfile/favreports`,
                'PUT',
                JSON.stringify(favorites.map((favorite) => favorite.reportKey))
            );
        },
        [api, instance]
    );

    const addFavoriteReport = useCallback(
        (reportToAdd: FavoriteReport): void => {
            setFavoriteReports((prev) => {
                const newValue = [...prev, reportToAdd];
                saveFavoritesList(newValue);

                return newValue;
            });
        },
        [saveFavoritesList]
    );

    const removeFavoriteReport = useCallback(
        (reportToRemove: FavoriteReport): void => {
            setFavoriteReports((prev) => {
                const newValue = prev.filter(
                    (favoriteReport) => favoriteReport.reportKey !== reportToRemove?.reportKey
                );
                saveFavoritesList(newValue);

                return newValue;
            });
        },
        [saveFavoritesList]
    );

    return {
        favoriteReports,
        addFavoriteReport,
        removeFavoriteReport,
        fetchFavoriteReports,
        get waitForProfileLoad() {
            return initPromiseRef.current;
        },
        cdnToken,
        sourceSystems,
        setSourceSystems,
        isLoading: profileLoading,
        setIsLoading,
        isEccoviaUser,
        user,
        environmentConfig,
        failedToLoad,
    };
}

// ============================================
// STANDARD CLIENTINSIGHT ROLE CHECKING FUNCTIONS
// These are for customers where cwInsightsCustomer = false
// ============================================

export function isSourceSystemAdminOrHigher(userProfile: UserProfile): boolean {
    return isUserSourceSystemAdminOrHigher(userProfile.user, userProfile.isEccoviaUser);
}

export function isUserSourceSystemAdminOrHigher(user: User | undefined, isEccoviaUser: boolean): boolean {
    if (isEccoviaUser) return true;

    const roleKey = user?.roleKey;
    if (!roleKey) return false;

    return roleKey <= UserRoles['Source System Admin'];
}

export function isOrgAdminOrHigher(userProfile: UserProfile): boolean {
    if (userProfile.isEccoviaUser) return true;

    const roleKey = userProfile.user?.roleKey;
    if (!roleKey) return false;

    return roleKey <= UserRoles['Org Admin'];
}

export function isGlobalAdmin(userProfile: UserProfile): boolean {
    if (userProfile.isEccoviaUser) return true;

    const roleKey = userProfile.user?.roleKey;
    if (!roleKey) return false;

    return roleKey === UserRoles['Global Admin'] || roleKey === UserRoles['Insights Global Admin'];
}

export function isClientTrackCustomer(userProfile: UserProfile): boolean {
    return userProfile.environmentConfig.clientTrackUrl !== undefined && userProfile.environmentConfig.clientTrackUrl.length !== 0;
}

// ============================================
// CW INSIGHTS ROLE CHECKING FUNCTIONS
// These are for customers where cwInsightsCustomer = true
// ============================================

/**
 * Check if user has Insights Global Admin role
 * @param userProfile The user profile to check
 * @returns true if user is Insights Global Admin or is an Eccovia user
 */
export function isInsightsGlobalAdmin(userProfile: UserProfile): boolean {
    if (userProfile.isEccoviaUser) return true;

    return userProfile.user?.roleKey === UserRoles['Insights Global Admin'];
}

/**
 * Check if user is Insights Engineer or higher (Insights Global Admin)
 * @param userProfile The user profile to check
 * @returns true if user is Insights Engineer+ or is an Eccovia user
 */
export function isInsightsEngineerOrHigher(userProfile: UserProfile): boolean {
    if (userProfile.isEccoviaUser) return true;

    const roleKey = userProfile.user?.roleKey;
    if (!roleKey) return false;

    // CW Insights roles are 100+, lower value = higher privilege
    return roleKey >= 100 && roleKey <= UserRoles['Insights Engineer'];
}

/**
 * Check if user is Insights Analyst or higher
 * @param userProfile The user profile to check
 * @returns true if user is Insights Analyst+ or is an Eccovia user
 */
export function isInsightsAnalystOrHigher(userProfile: UserProfile): boolean {
    if (userProfile.isEccoviaUser) return true;

    const roleKey = userProfile.user?.roleKey;
    if (!roleKey) return false;

    return roleKey >= 100 && roleKey <= UserRoles['Insights Analyst'];
}

/**
 * Check if user is Insights Viewer or higher (any CW Insights role)
 * @param userProfile The user profile to check
 * @returns true if user has any CW Insights role or is an Eccovia user
 */
export function isInsightsViewerOrHigher(userProfile: UserProfile): boolean {
    if (userProfile.isEccoviaUser) return true;

    const roleKey = userProfile.user?.roleKey;
    if (!roleKey) return false;

    return roleKey >= 100 && roleKey <= UserRoles['Insights Viewer'];
}

// ============================================
// UNIFIED PERMISSION FUNCTIONS
// These work for both customer types and respect Eccovia user override
// ============================================

/**
 * Can user author/create/edit reports?
 * - Eccovia users: Always true
 * - Standard customers: Org Admin or higher (roleKey <= 30)
 * - CW Insights customers: Insights Analyst or higher (Insights Global Admin, Insights Engineer, Insights Analyst)
 */
export function canAuthorReports(userProfile: UserProfile): boolean {
    if (userProfile.isEccoviaUser) return true;

    const roleKey = userProfile.user?.roleKey;
    if (!roleKey) return false;

    if (userProfile.environmentConfig.cwInsightsCustomer) {
        return roleKey >= 100 && roleKey <= UserRoles['Insights Analyst'];
    }
    return roleKey <= UserRoles['Org Admin'];
}

/**
 * Can user manage team members?
 * - Eccovia users: Always true
 * - Standard customers: Org Admin or higher
 * - CW Insights customers: Insights Analyst or higher (Insights Global Admin, Insights Engineer, Insights Analyst)
 */
export function canManageTeamMembers(userProfile: UserProfile): boolean {
    if (userProfile.isEccoviaUser) return true;

    const roleKey = userProfile.user?.roleKey;
    if (!roleKey) return false;

    if (userProfile.environmentConfig.cwInsightsCustomer) {
        // Insights Analyst or higher can manage team members
        return roleKey >= 100 && roleKey <= UserRoles['Insights Analyst'];
    }
    return roleKey <= UserRoles['Org Admin'];
}

/**
 * Can user access admin section?
 * - Eccovia users: Always true
 * - Standard customers: Source System Admin or higher
 * - CW Insights customers: Insights Analyst or higher (for Report Categories access)
 */
export function canAccessAdmin(userProfile: UserProfile): boolean {
    if (userProfile.isEccoviaUser) return true;

    const roleKey = userProfile.user?.roleKey;
    if (!roleKey) return false;

    if (userProfile.environmentConfig.cwInsightsCustomer) {
        // Insights Analyst or higher can access admin (for Report Categories)
        return roleKey >= 100 && roleKey <= UserRoles['Insights Analyst'];
    }
    return roleKey <= UserRoles['Source System Admin'];
}

/**
 * Can user perform analysis (filter, drill-down) on reports?
 * - Eccovia users: Always true
 * - Standard customers: All roles can analyze
 * - CW Insights customers: Insights Analyst or higher
 */
export function canAnalyzeReports(userProfile: UserProfile): boolean {
    if (userProfile.isEccoviaUser) return true;

    const roleKey = userProfile.user?.roleKey;
    if (!roleKey) return false;

    // Standard customers: all roles can analyze
    if (!userProfile.environmentConfig.cwInsightsCustomer) return true;

    // CW Insights: Analyst or higher
    return roleKey >= 100 && roleKey <= UserRoles['Insights Analyst'];
}

/**
 * Can user view monitoring features?
 * - Eccovia users: Always true
 * - Standard customers: Source System Admin or higher
 * - CW Insights customers: Insights Analyst or higher (for Usage Report access)
 */
export function canViewMonitoring(userProfile: UserProfile): boolean {
    if (userProfile.isEccoviaUser) return true;

    const roleKey = userProfile.user?.roleKey;
    if (!roleKey) return false;

    if (userProfile.environmentConfig.cwInsightsCustomer) {
        // Insights Analyst or higher can view monitoring (for Usage Report access)
        return roleKey >= 100 && roleKey <= UserRoles['Insights Analyst'];
    }
    return roleKey <= UserRoles['Source System Admin'];
}

/**
 * Check if user's role is valid for their customer type
 * - Eccovia users: Always valid (super-admin override)
 * - Standard customers: Must have a standard role (1-99)
 * - CW Insights customers: Must have a CW Insights role (100+)
 */
export function isRoleValidForCustomerType(userProfile: UserProfile): boolean {
    if (userProfile.isEccoviaUser) return true;

    const roleKey = userProfile.user?.roleKey;
    if (!roleKey) return false;

    const isCWInsights = userProfile.environmentConfig.cwInsightsCustomer;
    const hasInsightsRole = isCWInsightsRole(roleKey);

    // Role type must match customer type
    return isCWInsights ? hasInsightsRole : !hasInsightsRole;
}

/**
 * Can user view Usage Report?
 * - Eccovia users: Always true
 * - Standard customers: Global Admin only
 * - CW Insights customers: Insights Analyst or higher (Insights Global Admin, Insights Engineer, Insights Analyst)
 */
export function canViewUsageReport(userProfile: UserProfile): boolean {
    if (userProfile.isEccoviaUser) return true;

    const roleKey = userProfile.user?.roleKey;
    if (!roleKey) return false;

    if (userProfile.environmentConfig.cwInsightsCustomer) {
        return roleKey >= 100 && roleKey <= UserRoles['Insights Analyst'];
    }
    return roleKey === UserRoles['Global Admin'];
}

/**
 * Can user manage report categories?
 * - Eccovia users: Always true
 * - Standard customers: Global Admin only (via canAccessAdmin)
 * - CW Insights customers: Insights Analyst or higher (Insights Global Admin, Insights Engineer, Insights Analyst)
 */
export function canManageReportCategories(userProfile: UserProfile): boolean {
    if (userProfile.isEccoviaUser) return true;

    const roleKey = userProfile.user?.roleKey;
    if (!roleKey) return false;

    if (userProfile.environmentConfig.cwInsightsCustomer) {
        return roleKey >= 100 && roleKey <= UserRoles['Insights Analyst'];
    }
    // Standard customers: use existing canAccessAdmin (Source System Admin or higher)
    return roleKey <= UserRoles['Source System Admin'];
}

/**
 * Can user publish their own reports?
 * - Eccovia users: Always true
 * - Standard customers: Org Admin or higher
 * - CW Insights customers: Insights Analyst or higher (Insights Global Admin, Insights Engineer, Insights Analyst)
 */
export function canPublishOwnReports(userProfile: UserProfile): boolean {
    if (userProfile.isEccoviaUser) return true;

    const roleKey = userProfile.user?.roleKey;
    if (!roleKey) return false;

    if (userProfile.environmentConfig.cwInsightsCustomer) {
        return roleKey >= 100 && roleKey <= UserRoles['Insights Analyst'];
    }
    return roleKey <= UserRoles['Org Admin'];
}

/**
 * Can user delete or publish reports? Returns the boolean for delete/publish permission.
 * - Eccovia users: Can delete/publish any report
 * - Standard customers: Global Admin can delete/publish any, Org Admin can delete/publish own only
 * - CW Insights customers:
 *   - Insights Global Admin can delete/publish any report
 *   - Insights Engineer and Insights Analyst can only delete/publish their own reports
 */
export function canDeleteOrPublishReports(userProfile: UserProfile, createdByUser: boolean): boolean {
    if (userProfile.isEccoviaUser) return true;

    const roleKey = userProfile.user?.roleKey;
    if (!roleKey) return false;

    if (userProfile.environmentConfig.cwInsightsCustomer) {
        // Insights Global Admin can delete any report
        if (roleKey === UserRoles['Insights Global Admin']) return true;
        // Insights Engineer and Analyst can only delete their own reports
        if (roleKey >= 100 && roleKey <= UserRoles['Insights Analyst']) {
            return createdByUser;
        }
        return false;
    }
    // Standard customers: Global Admin can delete any, Org Admin+ can delete own
    if (roleKey === UserRoles['Global Admin']) return true;
    if (roleKey <= UserRoles['Org Admin']) return createdByUser;
    return false;
}

/**
 * Can user change report owner? Returns the type of permission.
 * - Eccovia users: Can change any report owner
 * - Standard customers: Global Admin can change any
 * - CW Insights customers:
 *   - Insights Global Admin can change any report owner
 *   - Insights Engineer and Insights Analyst can only change owner of their own reports
 */
export function canChangeReportOwner(userProfile: UserProfile, createdByUser: boolean): boolean {
    if (userProfile.isEccoviaUser) return true;

    const roleKey = userProfile.user?.roleKey;
    if (!roleKey) return false;

    if (userProfile.environmentConfig.cwInsightsCustomer) {
        // Insights Global Admin can change any report owner
        if (roleKey === UserRoles['Insights Global Admin']) return true;
        // Insights Engineer and Analyst can only change owner of their own reports
        if (roleKey >= 100 && roleKey <= UserRoles['Insights Analyst']) {
            return createdByUser;
        }
        return false;
    }
    // Standard customers: Global Admin only
    return roleKey === UserRoles['Global Admin'];
}

/**
 * Check if user has highest admin level for their customer type
 * - Eccovia users: Always true
 * - Standard customers: Global Admin
 * - CW Insights customers: Insights Global Admin
 */
export function isHighestAdmin(userProfile: UserProfile): boolean {
    if (userProfile.isEccoviaUser) return true;

    const roleKey = userProfile.user?.roleKey;
    if (!roleKey) return false;

    if (userProfile.environmentConfig.cwInsightsCustomer) {
        return roleKey === UserRoles['Insights Global Admin'];
    }
    return roleKey === UserRoles['Global Admin'];
}
